import React from "react";
import ProductPreview from "@/components/ProductPreview";

/**
 * Industry-specific cover + before/after visual pair for a case study.
 * Uses the same SVG-styled mockups as product previews so nothing is a stock image.
 * Falls back gracefully when a client_image_url is provided.
 */

const INDUSTRY_MAP = {
  Healthcare: "hospital",
  Health: "hospital",
  Retail: "restaurant",
  Hospitality: "hotel",
  "Hotels & Hospitality": "hotel",
  Education: "school",
  "Education / SaaS": "lms",
  Manufacturing: "manufacturing",
  Logistics: "logistics",
  "Real Estate": "property",
  "Banking & Fintech": "bank",
  Banking: "bank",
  "Direct Selling": "mlm",
  "MLM & Direct Selling": "mlm",
  HR: "hr",
  Home: "home",
};

function pickPreviewKey(industry, fallback = "bank") {
  if (!industry) return fallback;
  return INDUSTRY_MAP[industry] || fallback;
}

function BeforePane() {
  // Deliberately messy "before" state — paper trails, spreadsheets, sticky notes
  return (
    <div data-testid="case-before" className="relative w-full h-full bg-neutral-100 p-3 overflow-hidden">
      <div className="absolute -top-4 -left-4 w-16 h-16 bg-amber-200 rotate-[-8deg] border border-amber-300" />
      <div className="absolute top-8 right-2 w-14 h-14 bg-pink-200 rotate-[6deg] border border-pink-300 flex items-center justify-center font-mono text-[8px] text-neutral-700 text-center leading-tight p-1">
        URGENT<br/>refund #482
      </div>
      <div className="relative bg-white border border-neutral-300 p-2 mt-6">
        <div className="flex items-center gap-1 mb-1.5">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="ml-2 font-mono text-[8px] text-neutral-500">reconciliation_final_v7_FINAL.xlsx</span>
        </div>
        <div className="grid grid-cols-5 gap-px bg-neutral-300">
          {Array.from({ length: 25 }).map((_, i) => (
            <div key={i} className={`h-3 text-[6px] font-mono px-1 flex items-center ${i % 7 === 0 ? "bg-red-50 text-red-600" : i % 5 === 2 ? "bg-amber-50 text-amber-700" : "bg-white text-neutral-600"}`}>
              {i % 3 === 0 ? "#ERR" : i % 4 === 0 ? "TBD" : `${((i + 1) * 41) % 990}`}
            </div>
          ))}
        </div>
        <div className="mt-1 font-mono text-[7px] text-neutral-400">Row 47 mismatch · manual override applied 3x</div>
      </div>
      <div className="absolute bottom-2 left-3 right-3 border-t-2 border-dashed border-neutral-400 pt-1 font-mono text-[7px] uppercase tracking-widest text-neutral-500">Before · manual, error-prone</div>
    </div>
  );
}

function AfterPane({ previewKey }) {
  return (
    <div data-testid="case-after" className="relative w-full h-full bg-white overflow-hidden">
      <ProductPreview preview_key={previewKey} />
      <div className="absolute bottom-1 left-3 right-3 font-mono text-[7px] uppercase tracking-widest text-[color:var(--brand)]">After · one system, live data</div>
    </div>
  );
}

export function CaseStudyCover({ item }) {
  const key = pickPreviewKey(item.industry);
  if (item.image_url) {
    return (
      <div data-testid="case-cover" className="mt-8 border border-[color:var(--line)] bg-[color:var(--paper-alt)] overflow-hidden aspect-[16/7]">
        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div data-testid="case-cover" className="mt-8 relative border border-[color:var(--line)] bg-[color:var(--paper-alt)] overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" aria-hidden />
      <div className="relative grid md:grid-cols-5 gap-0">
        <div className="md:col-span-3 p-6 md:p-10 flex flex-col justify-between min-h-[220px]">
          <div>
            <div className="eyebrow">Snapshot · production dashboard</div>
            <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl leading-tight text-[color:var(--ink)]">
              What {item.client} sees, every morning.
            </h3>
            <p className="mt-3 text-sm md:text-base text-neutral-700 max-w-md leading-relaxed">
              Anonymised view of the live dashboard powering {item.industry?.toLowerCase()} operations after go-live.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-4">
            {(item.metrics || []).slice(0, 3).map((m, i) => (
              <div key={i} className="border-l-2 border-[color:var(--brand)] pl-3">
                <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{m.label}</div>
                <div className="font-display font-black text-lg md:text-xl">{m.after}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-2 border-l border-[color:var(--line)] bg-white aspect-[16/10] md:aspect-auto md:min-h-[260px]">
          <ProductPreview preview_key={key} />
        </div>
      </div>
    </div>
  );
}

export default function CaseStudyMedia({ item }) {
  const key = pickPreviewKey(item.industry);
  return (
    <section data-testid="case-before-after" className="mt-12">
      <div className="eyebrow">Before &amp; after</div>
      <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl leading-tight">From spreadsheets to a single source of truth.</h3>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="border border-[color:var(--line)] bg-white overflow-hidden aspect-[16/10] shadow-[0_20px_40px_-30px_rgba(0,0,0,0.4)]">
          <BeforePane />
        </div>
        <div className="border-2 border-[color:var(--brand)] bg-white overflow-hidden aspect-[16/10] shadow-[0_20px_40px_-25px_rgba(30,136,240,0.45)]">
          <AfterPane previewKey={key} />
        </div>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        Illustrative mockups — real production screens shown under NDA during a demo.
      </p>
    </section>
  );
}
